import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Building2, MapPin, Phone, Mail } from 'lucide-react';
import { api } from '../../../../utils/api';
import InfoTooltip from '../../../../components/InfoTooltip';

const Step1RegionalOffice = ({ formData, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: regionalOffices, isLoading, error } = useQuery(
    ['regional-offices'],
    () => api.get('/fare/regional-offices'),
    {
      keepPreviousData: true
    }
  );

  // Handle different possible response structures
  let offices = [];
  if (regionalOffices?.data) {
    // Check if data is directly an array
    if (Array.isArray(regionalOffices.data)) {
      offices = regionalOffices.data;
    }
    // Check if data has a data property that is an array
    else if (regionalOffices.data.data && Array.isArray(regionalOffices.data.data)) {
      offices = regionalOffices.data.data;
    }
    // Check if data has an offices property that is an array
    else if (regionalOffices.data.offices && Array.isArray(regionalOffices.data.offices)) {
      offices = regionalOffices.data.offices;
    }
  }

  // Debug logging to see the actual structure
  console.log('Full response:', regionalOffices);
  console.log('Response data:', regionalOffices?.data);
  console.log('Nested data:', regionalOffices?.data?.data);
  console.log('Final offices array:', offices);


  const filteredOffices = offices.filter(office =>
    office.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    office.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOfficeSelect = (office) => {
    onChange({
      regionalOffice: office._id,
      regionCode: office.name.replace(/\s+/g, '_').toUpperCase()
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">Select Regional Office</h4>
        <p className="text-sm text-gray-600">
          Choose the regional office for which you want to create the fare configuration.
        </p>
      </div>

      {/* Search */}
      <div>
        <label className="form-label">Search Regional Offices</label>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input"
          placeholder="Search by office name or address..."
        />
      </div>
      {/* Office Selection */}
      <div>
        {/* <label className="form-label">Regional Office</label> */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="loading-spinner"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            Error loading regional offices. Please try again.
          </div>
        ) : (
          <div className="space-y-2">
            {offices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No regional offices found
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredOffices.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    No offices match your search
                  </div>
                ) : (
                  filteredOffices.map((office) => (
                    <div
                      key={office._id}
                      onClick={() => handleOfficeSelect(office)}
                      className={`p-3 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
                        formData.regionalOffice === office._id
                          ? 'bg-indigo-50 border-indigo-200'
                          : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          formData.regionalOffice === office._id
                            ? 'bg-indigo-500 text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {office.name}
                          </h4>
                          <div className="mt-1 space-y-1">
                            <div className="flex items-center text-xs text-gray-500">
                              <MapPin className="h-3 w-3 mr-1" />
                              <span className="truncate">{office.address}</span>
                            </div>
                            {office.contactInfo?.phone && (
                              <div className="flex items-center text-xs text-gray-500">
                                <Phone className="h-3 w-3 mr-1" />
                                <span>{office.contactInfo.phone}</span>
                              </div>
                            )}
                            {office.contactInfo?.email && (
                              <div className="flex items-center text-xs text-gray-500">
                                <Mail className="h-3 w-3 mr-1" />
                                <span className="truncate">{office.contactInfo.email}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {formData.regionalOffice === office._id && (
                          <div className="flex-shrink-0">
                            <div className="w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Office Summary */}
      {formData.regionalOffice && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h5 className="text-sm font-medium text-green-800 mb-2">Selected Office</h5>
          <div className="text-sm text-green-700">
            <p><strong>Office:</strong> {offices.find(o => o._id === formData.regionalOffice)?.name}</p>
            <p><strong>Region Code:</strong> {formData.regionCode}</p>
            <p><strong>Currency:</strong> {formData.currency}</p>
          </div>
        </div>
      )}

      {/* Region Code */}
      <div>
        <label className="form-label">
          Fare Code
          <InfoTooltip text="Unique identifier for this fare configuration. Use uppercase with underscores (e.g., MUMBAI_CENTRAL)" />
        </label>
        <input
          type="text"
          value={formData.regionCode}
          onChange={(e) => onChange({ regionCode: e.target.value })}
          className="input"
          placeholder="Enter region code (e.g., MUMBAI_CENTRAL)"
        />
        <p className="text-xs text-gray-500 mt-1">
          This will be used to identify the fare configuration
        </p>
      </div>

      {/* Currency */}
      <div>
        <label className="form-label">
          Currency
          <InfoTooltip text="Select the currency for all fare calculations in this region" />
        </label>
        <select
          value={formData.currency}
          onChange={(e) => onChange({ currency: e.target.value })}
          className="input"
        >
          <option value="INR">INR (Indian Rupee)</option>
          <option value="USD">USD (US Dollar)</option>
          <option value="EUR">EUR (Euro)</option>
          <option value="GBP">GBP (British Pound)</option>
        </select>
      </div>

      {/* Due Repay Cycle */}
      <div>
        <label className="form-label">
          Due Repay Cycle
          <InfoTooltip text="Set the payment cycle for drivers. Choose daily, weekly, or monthly repayment schedules" />
        </label>
        <div className="grid grid-cols-2 gap-4">
          <select
            value={formData.dueRepayCycle?.type || 'day'}
            onChange={(e) => {
              const type = e.target.value;
              let defaultValue = '20';
              if (type === 'weekly') defaultValue = 'monday';
              if (type === 'monthly') defaultValue = '10';
              onChange({ 
                dueRepayCycle: { type, value: defaultValue } 
              });
            }}
            className="input"
          >
            <option value="day">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          {formData.dueRepayCycle?.type === 'weekly' ? (
            <select
              value={formData.dueRepayCycle?.value || 'monday'}
              onChange={(e) => onChange({ 
                dueRepayCycle: { 
                  ...formData.dueRepayCycle, 
                  value: e.target.value 
                } 
              })}
              className="input"
            >
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thursday">Thursday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
            </select>
          ) : formData.dueRepayCycle?.type === 'monthly' ? (
            <input
              type="number"
              value={formData.dueRepayCycle?.value || '10'}
              onChange={(e) => onChange({ 
                dueRepayCycle: { 
                  ...formData.dueRepayCycle, 
                  value: e.target.value 
                } 
              })}
              className="input"
              placeholder="Day of month (1-31)"
              min="1"
              max="31"
            />
          ) : (
            <input
              type="number"
              value={formData.dueRepayCycle?.value || '20'}
              onChange={(e) => onChange({ 
                dueRepayCycle: { 
                  ...formData.dueRepayCycle, 
                  value: e.target.value 
                } 
              })}
              className="input"
              placeholder="Number of days"
              min="1"
            />
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {formData.dueRepayCycle?.type === 'day' && 'Repayment every N days'}
          {formData.dueRepayCycle?.type === 'weekly' && 'Repayment on selected day of week'}
          {formData.dueRepayCycle?.type === 'monthly' && 'Repayment on selected day of month'}
        </p>
      </div>

      {/* Trip Accept Duration */}
      <div>
        <label className="form-label">
          Trip Accept Duration (seconds)
          <InfoTooltip text="Maximum time (in seconds) a driver has to accept a trip request before it expires" />
        </label>
        <input
          type="number"
          value={formData.tripAcceptDuration || 30}
          onChange={(e) => onChange({ tripAcceptDuration: parseInt(e.target.value) || 30 })}
          className="input"
          placeholder="30"
          min="10"
          max="120"
        />
        <p className="text-xs text-gray-500 mt-1">
          Time in seconds for driver to accept a trip request
        </p>
      </div>

      
    </div>
  );
};

export default Step1RegionalOffice;
